import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  interval,
  map,
  Observable,
  startWith,
  Subscription,
  takeWhile,
} from 'rxjs';
import { LoginService } from '../services/login.service';
import { Injectable, Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { DB_User } from '../interfaces/user';
import { OrganizationService } from '../services/organization.service';
import { ThemeService } from '../services/theme.service';
import { pTicket } from '../interfaces/pTicket';
@Injectable({
  providedIn: 'root',
})
export class User {
  // persisted
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  // memory only
  private name: string | null = null;
  private mail: string | null = null;
  private organization: string | null = null;
  private organizations: string[] | null = [];
  private permissions!: string[];

  private isAuthenticated = new BehaviorSubject<boolean>(false);
  private sessionTimeLeft = new BehaviorSubject<number>(0);

  private readonly WARNINGTIME = 300;
  private SessionWarned = false;
  private sessionTimerSub?: Subscription;
  private themeService: ThemeService = new ThemeService();

  @Output() sessionWarning = new EventEmitter();

  constructor() {
    this.initFromStorage();
    this.listenForCrossTabChanges();
  }

  // =============================
  // INIT FROM LOCAL STORAGE
  // =============================
  private initFromStorage(): void {
    const storedAccess = localStorage.getItem('accessToken');
    const storedRefresh = localStorage.getItem('refreshToken');

    if (!storedAccess || !storedRefresh) {
      this.logout();
      return;
    }

    this.accessToken = storedAccess;
    this.refreshToken = storedRefresh;

    if (!this.hasValidToken()) {
      this.logout();
      return;
    }

    this.loadFromToken(storedAccess);

    this.isAuthenticated.next(true);
    this.sessionTimeLeft.next(this.getRemainingSeconds());
    this.startSessionTimer();
  }

  // =============================
  // DECODE TOKEN → LOAD MEMORY
  // =============================
  private loadFromToken(token: string): void {
    try {
      this.permissions = []
      const payload = JSON.parse(atob(token.split('.')[1]));

      this.name = payload.usr_name;
      this.mail = payload.usr_mail;
      this.organization = payload.org_name;
      this.permissions = payload.permissions;
      this.organizations = payload.organizations;

      // Dynamically switch primary/accent colors per user/org
      if (this.organization === 'MVIP') {
        this.themeService.setPrimaryAccent('#3f51b5', '#e91e63'); // Indigo/Pink
      } else if (this.organization === 'ControlQS') {
        this.themeService.setPrimaryAccent('#009688', '#ff9800'); // Teal/Orange
      }
      this.userSubject.next(this.getUser());
    } catch {
      this.logout();
    }
  }

  private userSubject = new BehaviorSubject<DB_User | null>(null);

  getUserStream(): Observable<DB_User | null> {
    return this.userSubject.asObservable();
  }

  // =============================
  // LOGIN
  // =============================
  setToken(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;

    this.SessionWarned = false;

    // store ONLY tokens
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.removeItem('sessionWarningDismissed');

    this.loadFromToken(accessToken);

    this.isAuthenticated.next(true);
    this.sessionTimeLeft.next(this.getRemainingSeconds());
    this.startSessionTimer();
  }

  // =============================
  // USER DATA (memory only)
  // =============================
  getUser(): DB_User {
    return {
      usr_NAME: this.name ?? 'null',
      usr_MAIL: this.mail ?? 'null',
      org_name: this.organization ?? 'null',
      permissions: this.permissions ?? 'null',
      organizations: this.organizations ?? [],
    };
  }

  // =============================
  // TOKEN HELPERS
  // =============================
  getAccessToken(): string | null {
    return this.accessToken;
  }

  private hasValidToken(): boolean {
    if (!this.accessToken) return false;

    try {
      const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  private getRemainingSeconds(): number {
    if (!this.accessToken) return 0;

    try {
      const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return Math.max(payload.exp - now, 0);
    } catch {
      return 0;
    }
  }

  // =============================
  // SESSION TIMER (unchanged logic)
  // =============================
  private startSessionTimer(): void {
    this.sessionTimerSub?.unsubscribe();

    this.sessionTimerSub = interval(1000)
      .pipe(
        startWith(0),
        map(() => this.getRemainingSeconds()),
        takeWhile((timeLeft) => timeLeft > 0),
      )
      .subscribe({
        next: (timeLeft) => {
          this.sessionTimeLeft.next(timeLeft);

          if (
            timeLeft <= this.WARNINGTIME &&
            timeLeft > 0 &&
            !this.SessionWarned &&
            !this.wasSessionWarningDismissed()
          ) {
            this.SessionWarned = true;
            this.sessionWarning.emit('logout warning');
          }
        },
        complete: () => this.handleSessionExpiration(),
      });
  }

  getRemainingSessionTime(): Observable<number> {
    return this.sessionTimeLeft.asObservable();
  }

  // =============================
  // LOGOUT
  // =============================
  logout(): void {
    this.sessionTimerSub?.unsubscribe();

    this.accessToken = null;
    this.refreshToken = null;

    this.name = null;
    this.mail = null;
    this.organization = null;
    this.permissions = [];
    this.organizations = [];

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionWarningDismissed');

    this.isAuthenticated.next(false);
  }

  handleSessionExpiration(): void {
    this.logout();
    window.location.reload();
  }

  // =============================
  // SESSION WARNING HELPERS
  // =============================
  private wasSessionWarningDismissed(): boolean {
    return localStorage.getItem('sessionWarningDismissed') === 'true';
  }

  dismissSessionWarning(): void {
    localStorage.setItem('sessionWarningDismissed', 'true');
  }

  getAuthStatus(): Observable<boolean> {
    return this.isAuthenticated.asObservable();
  }

  getHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.getAccessToken()}`,
    });
    return headers;
  }

  // =============================
  // CROSS-TAB SYNC
  // =============================
  private listenForCrossTabChanges(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'accessToken') {
        if (!event.newValue) {
          // Token was removed in another tab → log out here too
          this.logout();
          window.location.reload();
        } else if (event.newValue !== this.accessToken) {
          // Token changed (e.g. org swap) → reload context from new token
          this.accessToken = event.newValue;
          this.refreshToken = localStorage.getItem('refreshToken');
          this.loadFromToken(event.newValue);
          this.sessionTimeLeft.next(this.getRemainingSeconds());
          this.startSessionTimer();

          // Notify the rest of the app that user context changed
          this.isAuthenticated.next(true);
          window.location.reload()
        }
      }
    });
  }

  validateTicketPermission(permission: string, pticket: pTicket): boolean {
    const user = this.getUser();

    return (
      (user.org_name == pticket.pct_organization &&
        user.permissions?.includes(permission)) ||
      false
    );
  }

  validatePermission(permission: string): boolean {
    const user = this.getUser();
    return user.permissions?.includes(permission) || false;
  }
}
